// Single source of truth for the DE/EN URL pairs, title and description of
// the site's core pages (Home + legal pages). Feeds both each page's own
// useSeoHead() call (src/hooks/useSeoHead.ts) and the matching entries in
// prerenderRoutes.ts, so the path/title/description/hreflang for these pages
// only has to be edited in one place.
//
// The knowledge-base guide pages deliberately aren't modeled here: published
// articles + click-throughs live in src/articles.ts, the unpublished guides
// keep their PATH/TITLE/DESCRIPTION/hreflang/JSON-LD as page-local constants.

export const SITE_URL = 'https://pstax.at';

export interface RoutePair {
  key: string;
  de: { path: string; title: string; description: string };
  en: { path: string; title: string; description: string };
  indexable: boolean;
}

export const ROUTE_PAIRS: RoutePair[] = [
  {
    key: 'home',
    de: {
      path: '/de/',
      title: 'PSTax - Steuerberatung für Selbstständige in Österreich',
      description:
        'Steuerberatung für Selbstständige, Freelancer und kleine Unternehmen in Österreich. Unterstützung bei USt, SVS und Einkommensteuer.',
    },
    en: {
      path: '/en/',
      title: 'PSTax - English-speaking Tax Advisor in Austria',
      description:
        'Tax consulting for freelancers, expats and small businesses in Austria. VAT, SVS and income tax support in English.',
    },
    indexable: true,
  },
  {
    key: 'imprint',
    de: {
      path: '/de/impressum/',
      title: 'Impressum | PSTax',
      description: 'Rechtliche Unternehmensinformationen und Impressum von PSTax, Steuerberatung für Selbstständige in Österreich.',
    },
    en: {
      path: '/en/imprint/',
      title: 'Imprint | PSTax',
      description: 'Legal company information and imprint for PSTax, tax advisory services for freelancers and expats in Austria.',
    },
    indexable: true,
  },
  {
    key: 'privacy',
    de: {
      path: '/de/datenschutz/',
      title: 'Datenschutzerklärung | PSTax',
      description: 'Wie PSTax personenbezogene Daten erhebt, verwendet und schützt — unsere vollständige Datenschutzerklärung.',
    },
    en: {
      path: '/en/privacy-policy/',
      title: 'Privacy Policy | PSTax',
      description: 'How PSTax collects, uses, and protects your personal data — our full privacy policy for freelancers and expats in Austria.',
    },
    indexable: true,
  },
  {
    key: 'gtc',
    de: {
      path: '/de/agb/',
      title: 'Allgemeine Geschäftsbedingungen | PSTax',
      description: 'Die allgemeinen Geschäftsbedingungen für die Steuerberatungsleistungen von PSTax.',
    },
    en: {
      path: '/en/gtc/',
      title: 'General Terms & Conditions | PSTax',
      description: "The general terms and conditions governing PSTax's tax advisory services.",
    },
    indexable: true,
  },
];

export function hreflangFor(pair: RoutePair): { lang: string; href: string }[] {
  return [
    { lang: 'en', href: `${SITE_URL}${pair.en.path}` },
    { lang: 'de', href: `${SITE_URL}${pair.de.path}` },
    // German is the site's default language (see LanguageContext.tsx) — the
    // German URL is what a searcher with no matching language/region gets.
    { lang: 'x-default', href: `${SITE_URL}${pair.de.path}` },
  ];
}

export function getRoutePair(key: string): RoutePair {
  const pair = ROUTE_PAIRS.find((p) => p.key === key);
  if (!pair) throw new Error(`seoRoutes: no ROUTE_PAIRS entry for key "${key}"`);
  return pair;
}
